import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Heart, Plus, Loader2, DollarSign, TrendingUp, Users, AlertCircle, Trash2, Edit2, CheckCircle2, Upload, Download, Repeat, Award, Activity, Copy, CheckSquare, ChevronDown } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { BulkActionBar, MergeDialog } from './donors/BulkActionBar';
import { DuplicatesTab } from './donors/DuplicatesTab';
import { GiftsTab } from './donors/GiftsTab';
import { OverviewTab } from './donors/OverviewTab';
import { ReportsTab } from './donors/ReportsTab';
import { HealthTab } from './donors/HealthTab';
import { MissingContactTab } from './donors/MissingContactTab';
import { EventOrdersTab } from './donors/EventOrdersTab';
import { DonorCardsView, DonorTableView } from './donors/DonorViews';
import { PipelineBoard } from './donors/PipelineBoard';
import { ActionsTab } from './donors/ActionsTab';
import { PledgesTab } from './donors/PledgesTab';
import { EngagementBadge } from './donors/EngagementBadge';
import { SegmentsBar } from './donors/SegmentsBar';
import { EmailListEditor, AddressListEditor } from './donors/ContactFieldEditors';
import { EngagementTab } from './donors/EngagementTab';
import { useNavigate } from 'react-router-dom';

const STAGES = [
  { key: 'prospect', label: 'Prospect', color: 'bg-gray-100 text-gray-700' },
  { key: 'cultivating', label: 'Cultivating', color: 'bg-[#E3F2FD] text-[#0D47A1]' },
  { key: 'solicited', label: 'Solicited', color: 'bg-[#FFF3E0] text-[#E65100]' },
  { key: 'donor', label: 'Donor', color: 'bg-[#E8F5E9] text-[#1B5E20]' },
  { key: 'steward', label: 'Steward', color: 'bg-[#F3E5F5] text-[#4A148C]' },
  { key: 'lapsed', label: 'Lapsed', color: 'bg-[#FFEBEE] text-[#B71C1C]' },
];

const GIFT_TYPES = [
  { key: 'one_time', label: 'One-time' },
  { key: 'recurring', label: 'Recurring' },
  { key: 'in_kind', label: 'In-kind' },
  { key: 'pledge', label: 'Pledge' },
];

const fmt$ = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function DonorsPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [donors, setDonors] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [reviewOnly, setReviewOnly] = useState(false);
  const [selectedDonor, setSelectedDonor] = useState(null);
  const [donorDialogOpen, setDonorDialogOpen] = useState(false);
  const [editingDonor, setEditingDonor] = useState(null);
  const [giftDialogOpen, setGiftDialogOpen] = useState(false);
  const [editingGift, setEditingGift] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [stageFilter, setStageFilter] = useState('');
  const [tagFilter, setTagFilter] = useState([]);       // array of selected tags
  const [minAmount, setMinAmount] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [bucketFilter, setBucketFilter] = useState('');
  const [allTags, setAllTags] = useState([]);           // fetched from /donor-tags
  const [allYears, setAllYears] = useState([]);         // fetched from year histogram
  const [allCampaigns, setAllCampaigns] = useState([]); // fetched from /donor-campaigns
  const [viewMode, setViewMode] = useState('list');      // list | cards | table

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (reviewOnly) params.set('needs_review', 'true');
      if (sortBy) params.set('sort_by', sortBy);
      if (sortDir) params.set('sort_dir', sortDir);
      if (stageFilter) params.set('stage', stageFilter);
      if (tagFilter.length) params.set('tags', tagFilter.join(','));
      if (minAmount) params.set('min_amount', minAmount);
      if (yearFilter) params.set('year', yearFilter);
      if (campaignFilter) params.set('campaign', campaignFilter);
      if (bucketFilter) params.set('engagement_bucket', bucketFilter);
      const [d, g, s, t, y, c] = await Promise.all([
        api.get(`/donors${params.toString() ? `?${params}` : ''}`),
        api.get('/donor-gifts'),
        api.get('/donors/reports/summary'),
        api.get('/donor-tags'),
        api.get('/donor-gifts-year-histogram'),
        api.get('/donor-campaigns'),
      ]);
      setDonors(d.data);
      setGifts(g.data);
      setSummary(s.data);
      setAllTags(t.data);
      setAllYears(y.data);
      setAllCampaigns(c.data);
    } catch (e) {
      toast.error('Failed to load donor data');
    } finally { setLoading(false); }
  }, [api, q, reviewOnly, sortBy, sortDir, stageFilter, tagFilter, minAmount, yearFilter, campaignFilter, bucketFilter]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearSelection = () => setSelectedIds(new Set());
  const toggleSelectAll = () => setSelectedIds(prev =>
    prev.size === donors.length ? new Set() : new Set(donors.map(d => d.id))
  );
  const selectedDonors = donors.filter(d => selectedIds.has(d.id));

  if (loading && !summary) {
    return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin text-[#6B3FA0]" /></div>;
  }

  const donorGifts = selectedDonor ? gifts.filter(g => g.donor_id === selectedDonor.id) : [];
  const stageColor = (stage) => STAGES.find(s => s.key === stage)?.color || 'bg-gray-100 text-gray-700';
  const stageLabel = (stage) => STAGES.find(s => s.key === stage)?.label || stage;

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="donors-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Heart className="h-8 w-8 text-[#B71C5C]" fill="#B71C5C" />
          <div>
            <h1 className="text-4xl font-bold">Donors</h1>
            <p className="text-gray-500 text-sm">Confidential — siloed to authorized users only</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setEditingGift(null); setGiftDialogOpen(true); }}
                  disabled={donors.length === 0}
                  data-testid="add-gift-btn">
            <DollarSign className="h-4 w-4 mr-1" /> Log gift
          </Button>
          <Button onClick={() => { setEditingDonor(null); setDonorDialogOpen(true); }}
                  data-testid="add-donor-btn">
            <Plus className="h-4 w-4 mr-1" /> Add donor
          </Button>
        </div>
      </div>

      {/* KPI strip removed — now lives inside the Overview tab. */}

      {/* Tab bar */}
      <div className="flex gap-2 border-b mb-4 flex-wrap">
        {['overview', 'reports', 'donors', 'engagement', 'pipeline', 'actions', 'gifts', 'pledges', 'import', 'health', 'contact-fixes', 'events'].map(t => (
          <button key={t} onClick={() => setTab(t)}
                  className={`px-4 py-2 text-sm capitalize border-b-2 transition-colors ${tab === t ? 'border-[#6B3FA0] text-[#6B3FA0] font-semibold' : 'border-transparent text-gray-600'}`}
                  data-testid={`tab-${t}`}>
            {t === 'contact-fixes' ? 'contact fixes' : t}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab api={api} />}
      {tab === 'engagement' && <EngagementTab api={api} />}
      {tab === 'pipeline' && <PipelineBoard donors={donors} onChange={load} api={api} />}
      {tab === 'actions' && <ActionsTab api={api} donors={donors} onChange={load} />}
      {tab === 'pledges' && <PledgesTab api={api} />}

      {/* DONORS TAB */}
      {tab === 'donors' && (
        <>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Input placeholder="Search name, org, email…" value={q} onChange={e => setQ(e.target.value)}
                   className="max-w-xs" data-testid="donor-search" />
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                    className="border rounded px-2 py-1.5 text-sm bg-white" data-testid="donor-sort-by">
              <option value="name">Sort: First name</option>
              <option value="last_name">Sort: Last name</option>
              <option value="total_given">Sort: Total given</option>
              <option value="gift_count">Sort: Gift count</option>
              <option value="last_gift_date">Sort: Recent gift</option>
              <option value="created_at">Sort: Recently added</option>
              <option value="pipeline_stage">Sort: Pipeline stage</option>
              <option value="engagement_score">Sort: Engagement score</option>
            </select>
            <button onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                    className="border rounded px-2 py-1.5 text-sm bg-white hover:bg-gray-50"
                    data-testid="donor-sort-dir"
                    title={`Sort direction: ${sortDir === 'asc' ? 'ascending' : 'descending'}`}>
              {sortDir === 'asc' ? '▲' : '▼'}
            </button>
            <select value={stageFilter} onChange={e => setStageFilter(e.target.value)}
                    className="border rounded px-2 py-1.5 text-sm bg-white capitalize" data-testid="donor-stage-filter">
              <option value="">All stages</option>
              {['prospect', 'cultivating', 'solicited', 'donor', 'steward', 'lapsed'].map(s =>
                <option key={s} value={s} className="capitalize">{s}</option>
              )}
            </select>
            <select value={yearFilter} onChange={e => setYearFilter(e.target.value)}
                    className="border rounded px-2 py-1.5 text-sm bg-white" data-testid="donor-year-filter">
              <option value="">All years</option>
              {(allYears || []).slice().reverse().map(y =>
                <option key={y.year} value={y.year}>Gave in {y.year} ({y.gift_count})</option>
              )}
            </select>
            <select value={campaignFilter} onChange={e => setCampaignFilter(e.target.value)}
                    className="border rounded px-2 py-1.5 text-sm bg-white max-w-[180px] truncate"
                    data-testid="donor-campaign-filter">
              <option value="">All campaigns</option>
              {(allCampaigns || []).slice(0, 50).map(c =>
                <option key={c.campaign} value={c.campaign}>{c.campaign} ({c.gift_count})</option>
              )}
            </select>
            <select value={bucketFilter} onChange={e => setBucketFilter(e.target.value)}
                    className="border rounded px-2 py-1.5 text-sm bg-white" data-testid="donor-bucket-filter">
              <option value="">All engagement</option>
              <option value="champion">Champions</option>
              <option value="engaged">Engaged</option>
              <option value="at_risk">At risk</option>
              <option value="cold">Cold</option>
            </select>
            <Input type="number" placeholder="Min $ given" value={minAmount}
                   onChange={e => setMinAmount(e.target.value)}
                   className="w-28" data-testid="donor-min-amount" />
            <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer select-none px-2 py-1.5 border rounded bg-white"
                   data-testid="review-only-toggle-label">
              <input type="checkbox" checked={reviewOnly} onChange={e => setReviewOnly(e.target.checked)}
                     data-testid="review-only-toggle" />
              <AlertCircle className="h-3.5 w-3.5 text-orange-600" /> Needs review
            </label>
            {(q || stageFilter || tagFilter.length || minAmount || yearFilter || campaignFilter || bucketFilter || reviewOnly) && (
              <button onClick={() => { setQ(''); setStageFilter(''); setTagFilter([]); setMinAmount(''); setYearFilter(''); setCampaignFilter(''); setBucketFilter(''); setReviewOnly(false); }}
                      className="text-xs text-red-600 hover:underline px-1"
                      data-testid="donor-clear-filters">
                Clear filters
              </button>
            )}
            <div className="ml-auto flex gap-2">
              <QuickSelectMenu donors={donors} onSelect={(ids) => setSelectedIds(new Set(ids))} />
              <Button variant="outline" size="sm" onClick={() => setCleanupOpen(true)}
                      data-testid="cleanup-btn">
                <Repeat className="h-3.5 w-3.5 mr-1" /> Cleanup
              </Button>
            </div>
          </div>

          {/* Tag chip filters */}
          {allTags.length > 0 && (
            <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="tag-chip-row">
              <span className="text-xs text-gray-500 uppercase mr-1">Categories</span>
              {allTags.slice(0, 20).map(t => {
                const active = tagFilter.includes(t.tag);
                return (
                  <button key={t.tag}
                          onClick={() => setTagFilter(f => active ? f.filter(x => x !== t.tag) : [...f, t.tag])}
                          className={`text-xs px-2 py-0.5 rounded-full border transition-colors
                            ${active ? 'bg-[#6B3FA0] text-white border-[#6B3FA0]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                          data-testid={`tag-chip-${t.tag.replace(/\s/g, '-')}`}>
                    {t.tag} <span className="opacity-70">({t.count})</span>
                  </button>
                );
              })}
              {tagFilter.length > 0 && (
                <button onClick={() => setTagFilter([])} className="text-xs text-red-600 hover:underline ml-1"
                        data-testid="tag-filter-clear">Clear</button>
              )}
            </div>
          )}

          <SegmentsBar api={api}
                       currentFilters={{ q, stage: stageFilter, tags: tagFilter, min_amount: minAmount,
                                          sort_by: sortBy, sort_dir: sortDir, needs_review: reviewOnly }}
                       onApply={(f) => {
                         setQ(f.q || '');
                         setStageFilter(f.stage || '');
                         setTagFilter(f.tags || []);
                         setMinAmount(f.min_amount || '');
                         setSortBy(f.sort_by || 'name');
                         setSortDir(f.sort_dir || 'asc');
                         setReviewOnly(!!f.needs_review);
                       }}
                       onCleared={() => {
                         setQ(''); setStageFilter(''); setTagFilter([]); setMinAmount('');
                         setSortBy('name'); setSortDir('asc'); setReviewOnly(false);
                       }} />

          <BulkActionBar api={api} selectedIds={selectedIds}
                         allSelected={selectedIds.size === donors.length && donors.length > 0}
                         totalVisible={donors.length}
                         onToggleAll={toggleSelectAll}
                         onClearSelection={clearSelection}
                         onOpenMerge={() => setMergeOpen(true)}
                         onDone={() => { clearSelection(); load(); }} />

          {donors.length === 0 ? (
            <Card><CardContent className="p-12 text-center text-gray-500">
              {reviewOnly ? 'No donors need review — the list is clean.' : 'No donors yet. Click "Add donor" to get started.'}
            </CardContent></Card>
          ) : (
            <>
              <div className="flex items-center gap-1 mb-2" data-testid="donor-view-switch">
                {['list', 'cards', 'table'].map(v => (
                  <button key={v} onClick={() => setViewMode(v)}
                          className={`text-xs px-3 py-1 rounded-full capitalize border transition-colors ${viewMode === v ? 'bg-[#6B3FA0] text-white border-[#6B3FA0]' : 'bg-white text-gray-600 border-gray-300 hover:border-[#6B3FA0]'}`}
                          data-testid={`donor-view-${v}`}>
                    {v}
                  </button>
                ))}
              </div>
              {viewMode === 'cards' && (
                <DonorCardsView donors={donors} navigate={navigate} stageColor={stageColor} stageLabel={stageLabel} />
              )}
              {viewMode === 'table' && (
                <DonorTableView donors={donors} navigate={navigate} stageColor={stageColor} stageLabel={stageLabel}
                                sortBy={sortBy} sortDir={sortDir}
                                onSort={(key) => {
                                  if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                                  else { setSortBy(key); setSortDir(['name', 'last_name', 'pipeline_stage'].includes(key) ? 'asc' : 'desc'); }
                                }} />
              )}
              {viewMode === 'list' && (
            <div className="grid gap-2">
              {donors.map(d => {
                const initials = ((d.first_name || d.name || '?')[0] + (d.last_name?.[0] || '')).toUpperCase();
                const sliceActive = yearFilter || campaignFilter;
                const primaryAmount = sliceActive && d.slice_total != null ? d.slice_total : d.total_given;
                return (
                <Card key={d.id}
                      className={`hover:shadow-md transition-shadow ${selectedIds.has(d.id) ? 'ring-2 ring-[#6B3FA0]' : ''}`}
                      data-testid={`donor-row-${d.id}`}>
                  <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                    <input type="checkbox" checked={selectedIds.has(d.id)}
                           onChange={(e) => { e.stopPropagation(); toggleSelect(d.id); }}
                           onClick={e => e.stopPropagation()}
                           className="h-4 w-4 shrink-0 cursor-pointer"
                           data-testid={`donor-select-${d.id}`} />
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#6B3FA0] to-[#B71C5C] text-white flex items-center justify-center font-semibold text-sm shrink-0"
                         onClick={() => navigate(`/donors/${d.id}`)}>
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/donors/${d.id}`)}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{d.name}</span>
                        {d.needs_review && <Badge className="bg-orange-100 text-orange-800 border-0 text-xs" title={`Missing: ${(d.missing_fields || []).join(', ') || 'unknown'}`} data-testid={`needs-review-${d.id}`}>
                          <AlertCircle className="h-3 w-3 mr-0.5" /> review
                        </Badge>}
                        {d.engagement_bucket && <EngagementBadge score={d.engagement_score} bucket={d.engagement_bucket} />}
                        <Badge className={`${stageColor(d.pipeline_stage)} border-0 text-xs`}>{stageLabel(d.pipeline_stage)}</Badge>
                        {d.org && <span className="text-xs text-gray-500">· {d.org}</span>}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 truncate">
                        {d.email || <span className="text-orange-600">— no email —</span>}{d.phone ? ` · ${d.phone}` : ''}
                        {d.last_gift_date && <span className="ml-1">· last gift {d.last_gift_date}</span>}
                        {(d.tags || []).length > 0 && <span> · {d.tags.slice(0, 3).join(', ')}{d.tags.length > 3 ? ` +${d.tags.length - 3}` : ''}</span>}
                      </div>
                    </div>
                    <div className="text-right cursor-pointer" onClick={() => navigate(`/donors/${d.id}`)}>
                      <div className="font-bold text-lg tabular-nums">{fmt$(primaryAmount)}</div>
                      <div className="text-[10px] text-gray-500 uppercase">
                        {sliceActive ? `in ${yearFilter || campaignFilter}` : `${d.gift_count} gift${d.gift_count !== 1 ? 's' : ''}`}
                      </div>
                    </div>
                    <Button size="sm" variant="outline"
                            onClick={(e) => { e.stopPropagation(); setEditingDonor(d); setDonorDialogOpen(true); }}
                            data-testid={`edit-donor-${d.id}`}
                            title="Edit donor (name, email, tags, stage, notes)">
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                  </CardContent>
                </Card>
                );
              })}
            </div>
              )}
            </>
          )}

          <MergeDialog open={mergeOpen} onClose={() => setMergeOpen(false)}
                       api={api} donors={selectedDonors}
                       onMerged={() => { clearSelection(); load(); }} />

          {/* Cleanup dialog — Duplicates surface without a full tab */}
          <Dialog open={cleanupOpen} onOpenChange={o => !o && setCleanupOpen(false)}>
            <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="cleanup-dialog">
              <DialogHeader><DialogTitle>Cleanup — duplicate donors</DialogTitle></DialogHeader>
              <DuplicatesTab api={api} onMerged={load} />
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* GIFTS TAB */}
      {tab === 'gifts' && (
        <GiftsTab api={api} gifts={gifts} onChange={load}
                  setEditingGift={setEditingGift} setGiftDialogOpen={setGiftDialogOpen} />
      )}

      {/* REPORTS TAB */}
      {tab === 'reports' && <ReportsTab api={api} />}

      {/* HEALTH TAB */}
      {tab === 'health' && <HealthTab api={api} onChanged={load} onOpenContactFixes={() => setTab('contact-fixes')} />}
      {tab === 'contact-fixes' && <MissingContactTab api={api} onChanged={load} />}

      {/* EVENTS TAB */}
      {tab === 'events' && <EventOrdersTab api={api} />}

      {/* IMPORT TAB */}
      {tab === 'import' && <ImportTab api={api} onImported={load} />}

      {/* Donor Detail Dialog */}
      <Dialog open={!!selectedDonor} onOpenChange={o => !o && setSelectedDonor(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="donor-detail">
          {selectedDonor && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  {selectedDonor.name}
                  <Badge className={`${stageColor(selectedDonor.pipeline_stage)} border-0`}>{stageLabel(selectedDonor.pipeline_stage)}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2 text-sm">
                {selectedDonor.org && <div><span className="text-gray-500">Org:</span> {selectedDonor.org}</div>}
                <div><span className="text-gray-500">Email:</span> {selectedDonor.email || '—'}</div>
                <div><span className="text-gray-500">Phone:</span> {selectedDonor.phone || '—'}</div>
                {selectedDonor.address && <div><span className="text-gray-500">Address:</span> {selectedDonor.address}</div>}
                {selectedDonor.tags?.length > 0 && <div><span className="text-gray-500">Tags:</span> {selectedDonor.tags.join(', ')}</div>}
                {selectedDonor.notes && <div className="p-3 bg-gray-50 rounded whitespace-pre-wrap">{selectedDonor.notes}</div>}
                <div className="border-t pt-3">
                  <div className="font-semibold mb-2">Gifts ({donorGifts.length}) — {fmt$(selectedDonor.total_given)} total</div>
                  {donorGifts.map(g => (
                    <div key={g.id} className="flex justify-between text-xs py-1 border-b last:border-0">
                      <span>{g.date} · {g.campaign || '—'}</span>
                      <span className="font-medium">{fmt$(g.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter className="flex-wrap gap-2">
                <Button variant="outline" onClick={() => { setEditingDonor(selectedDonor); setDonorDialogOpen(true); setSelectedDonor(null); }} data-testid="edit-donor-btn">
                  <Edit2 className="h-4 w-4 mr-1" /> Edit
                </Button>
                <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={async () => {
                          if (!window.confirm(`Delete ${selectedDonor.name} and all associated gifts?`)) return;
                          try {
                            await api.delete(`/donors/${selectedDonor.id}`);
                            toast.success('Donor deleted');
                            setSelectedDonor(null); load();
                          } catch (e) { toast.error('Delete failed'); }
                        }}
                        data-testid="delete-donor-btn">
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
                <Button onClick={() => setSelectedDonor(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <DonorDialog open={donorDialogOpen} onClose={() => setDonorDialogOpen(false)}
                   api={api} editing={editingDonor} onSaved={load} />
      <GiftDialog open={giftDialogOpen} onClose={() => setGiftDialogOpen(false)}
                  api={api} editing={editingGift} donors={donors} onSaved={load} />
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, variant, testid }) {
  const border = variant === 'warn' ? 'border-orange-300 bg-orange-50' : '';
  return (
    <Card className={border}><CardContent className="p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-gray-500 uppercase mb-1"><Icon className="h-4 w-4" />{label}</div>
      <div className="text-2xl font-bold">{value}</div>
    </CardContent></Card>
  );
}

// Quick-select menu — one-click select common cohorts for cleanup
function QuickSelectMenu({ donors, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const pick = (predicate, label) => {
    const ids = donors.filter(predicate).map(d => d.id);
    onSelect(ids);
    setOpen(false);
    toast.success(`${ids.length} selected · ${label}`);
  };

  const options = [
    { label: 'All visible donors',  fn: () => true },
    { label: 'Needs review',        fn: (d) => d.needs_review },
    { label: 'Missing email',       fn: (d) => !(d.email || '').trim() },
    { label: 'Never gave',          fn: (d) => (d.gift_count || 0) === 0 },
    { label: 'Champions',           fn: (d) => d.engagement_bucket === 'champion' },
    { label: 'Engaged',             fn: (d) => d.engagement_bucket === 'engaged' },
    { label: 'At risk',             fn: (d) => d.engagement_bucket === 'at_risk' },
    { label: 'Cold donors',         fn: (d) => d.engagement_bucket === 'cold' },
    { label: 'Lapsed stage',        fn: (d) => d.pipeline_stage === 'lapsed' },
  ];

  return (
    <div ref={ref} className="relative">
      <Button variant="outline" size="sm" onClick={() => setOpen(o => !o)}
              data-testid="quick-select-btn">
        <CheckSquare className="h-3.5 w-3.5 mr-1" /> Quick select
        <ChevronDown className="h-3 w-3 ml-1" />
      </Button>
      {open && (
        <div className="absolute right-0 mt-1 w-52 bg-white border rounded-md shadow-lg z-30 py-1"
             data-testid="quick-select-menu">
          {options.map(o => {
            const count = donors.filter(o.fn).length;
            return (
              <button key={o.label}
                      onClick={() => pick(o.fn, o.label)}
                      disabled={count === 0}
                      className="w-full flex items-center justify-between text-left px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`quick-select-${o.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <span>{o.label}</span>
                <span className="text-xs text-gray-400 tabular-nums">{count}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}



function DonorDialog({ open, onClose, api, editing, onSaved }) {
  const empty = { first_name: '', last_name: '', org: '', phone: '', pipeline_stage: 'prospect', tags: '', notes: '', emails: [], addresses: [] };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        first_name: editing.first_name || '',
        last_name: editing.last_name || '',
        org: editing.org || '',
        phone: editing.phone || '',
        pipeline_stage: editing.pipeline_stage || 'prospect',
        tags: (editing.tags || []).join(', '),
        notes: editing.notes || '',
        emails: (editing.emails || []).map(e => ({ ...e })),
        addresses: (editing.addresses || []).map(a => ({ ...a })),
      });
    } else if (open) setForm(empty);
  }, [editing, open]);

  const save = async () => {
    if (!form.first_name.trim() && !form.last_name.trim()) { toast.error('First or last name required'); return; }
    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        org: form.org,
        phone: form.phone,
        pipeline_stage: form.pipeline_stage,
        notes: form.notes,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        emails: (form.emails || []).filter(e => (e.email || '').trim()),
        addresses: form.addresses || [],
      };
      if (editing) await api.put(`/donors/${editing.id}`, payload);
      else await api.post('/donors', payload);
      toast.success(editing ? 'Donor updated' : 'Donor added');
      onSaved(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="donor-dialog">
        <DialogHeader><DialogTitle>{editing ? 'Edit donor' : 'Add donor'}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>First name *</Label><Input value={form.first_name} onChange={e => setField('first_name', e.target.value)} data-testid="donor-first-name" /></div>
            <div><Label>Last name</Label><Input value={form.last_name} onChange={e => setField('last_name', e.target.value)} data-testid="donor-last-name" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Organization</Label><Input value={form.org} onChange={e => setField('org', e.target.value)} data-testid="donor-org" /></div>
            <div>
              <Label>Stage</Label>
              <select value={form.pipeline_stage} onChange={e => setField('pipeline_stage', e.target.value)}
                      className="w-full border rounded px-3 py-2" data-testid="donor-stage">
                {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={e => setField('phone', e.target.value)} data-testid="donor-phone" /></div>
          <div><Label>Emails</Label><EmailListEditor emails={form.emails} onChange={emails => setField('emails', emails)} /></div>
          <div><Label>Addresses</Label><AddressListEditor addresses={form.addresses} onChange={addresses => setField('addresses', addresses)} /></div>
          <div><Label>Tags (comma-separated)</Label><Input value={form.tags} onChange={e => setField('tags', e.target.value)} placeholder="major-gift, board-member" data-testid="donor-tags" /></div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setField('notes', e.target.value)} rows={4} data-testid="donor-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-donor-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GiftDialog({ open, onClose, api, editing, donors, onSaved }) {
  const empty = { donor_id: '', date: new Date().toISOString().slice(0, 10), amount: '', campaign: '', gift_type: 'one_time', notes: '', acknowledged: false };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) setForm({ ...empty, ...editing, amount: String(editing.amount) });
    else if (open) setForm(empty);
  }, [editing, open]);

  const save = async () => {
    if (!form.donor_id || !form.amount) { toast.error('Donor and amount required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (editing) await api.put(`/donor-gifts/${editing.id}`, payload);
      else await api.post('/donor-gifts', payload);
      toast.success(editing ? 'Gift updated' : 'Gift logged');
      onSaved(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };
  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="gift-dialog">
        <DialogHeader><DialogTitle>{editing ? 'Edit gift' : 'Log gift'}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Donor *</Label>
            <select value={form.donor_id} onChange={e => setField('donor_id', e.target.value)}
                    className="w-full border rounded px-3 py-2" data-testid="gift-donor" disabled={!!editing}>
              <option value="">Choose donor…</option>
              {donors.map(d => <option key={d.id} value={d.id}>{d.name}{d.org ? ` — ${d.org}` : ''}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Date *</Label><Input type="date" value={form.date} onChange={e => setField('date', e.target.value)} data-testid="gift-date" /></div>
            <div><Label>Amount *</Label><Input type="number" step="0.01" min="0" value={form.amount} onChange={e => setField('amount', e.target.value)} data-testid="gift-amount" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Campaign</Label><Input value={form.campaign} onChange={e => setField('campaign', e.target.value)} data-testid="gift-campaign" /></div>
            <div>
              <Label>Type</Label>
              <select value={form.gift_type} onChange={e => setField('gift_type', e.target.value)}
                      className="w-full border rounded px-3 py-2" data-testid="gift-type">
                {GIFT_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setField('notes', e.target.value)} rows={2} data-testid="gift-notes" /></div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.acknowledged} onChange={e => setField('acknowledged', e.target.checked)}
                   data-testid="gift-acked" />
            Acknowledged (thank-you sent)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-gift-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== Dashboard Tab ====================

function DashboardTab({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/donors/reports/dashboard');
      setData(data);
    } catch (e) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const markAcked = async (giftId) => {
    try {
      await api.put(`/donor-gifts/${giftId}/acknowledge`);
      toast.success('Marked as thanked');
      load();
    } catch { toast.error('Failed to mark as thanked'); }
  };

  if (loading || !data) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#B71C5C]" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="dashboard-tab">
      {/* Overdue acknowledgements banner */}
      {(data.overdue_acks || []).length > 0 && (
        <Card className="border-orange-300 bg-orange-50">
          <CardContent className="p-4" data-testid="overdue-acks-banner">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              <div className="font-semibold text-orange-900">
                {data.overdue_acks.length} gift{data.overdue_acks.length === 1 ? '' : 's'} overdue for thank-you ({'>'}{data.ack_due_hours || 72}h)
              </div>
            </div>
            <ul className="space-y-1.5 max-h-[180px] overflow-y-auto">
              {data.overdue_acks.map(g => (
                <li key={g.id} className="flex items-center justify-between text-sm bg-white rounded px-2 py-1.5">
                  <span>
                    <span className="font-medium">{g.donor_name}</span>
                    <span className="text-gray-500"> · {fmt$(g.amount)} · {g.date}</span>
                  </span>
                  <Button size="sm" variant="outline"
                          onClick={() => markAcked(g.id)}
                          className="h-7 text-xs border-green-500 text-green-700 hover:bg-green-50"
                          data-testid={`mark-thanked-${g.id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark thanked
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
      {/* Row 1: Retention + Avg + Forecast */}
      <div className="grid md:grid-cols-3 gap-3">
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 text-xs text-gray-500 uppercase mb-1"><Repeat className="h-4 w-4" /> Retention rate</div>
          <div className="text-3xl font-bold" data-testid="retention-rate">{data.retention.rate_pct}%</div>
          <div className="text-xs text-gray-500 mt-1">{data.retention.retained} / {data.retention.last_year_donors} last-year donors gave again</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 text-xs text-gray-500 uppercase mb-1"><DollarSign className="h-4 w-4" /> Average gift (YTD)</div>
          <div className="text-3xl font-bold" data-testid="avg-gift">{fmt$(data.avg_gift)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 text-xs text-gray-500 uppercase mb-1"><TrendingUp className="h-4 w-4" /> Recurring this month</div>
          <div className="text-3xl font-bold" data-testid="recurring-forecast">{fmt$(data.recurring_forecast_this_month)}</div>
          <div className="text-xs text-gray-500 mt-1">expected/projected</div>
        </CardContent></Card>
      </div>

      {/* Giving over time */}
      <Card><CardContent className="p-4">
        <div className="font-semibold mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Giving — last 12 months</div>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={data.monthly_series} margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => fmt$(v)} />
            <Line type="monotone" dataKey="total" stroke="#B71C5C" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent></Card>

      {/* Row 3: Campaigns + Major donors */}
      <div className="grid md:grid-cols-2 gap-3">
        <Card><CardContent className="p-4">
          <div className="font-semibold mb-3">Top campaigns</div>
          {data.campaign_totals.length === 0 ? <p className="text-sm text-gray-500">No campaigns yet.</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.campaign_totals} layout="vertical" margin={{ top: 5, right: 15, left: 80, bottom: 5 }}>
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="campaign" tick={{ fontSize: 11 }} width={90} />
                <Tooltip formatter={(v) => fmt$(v)} />
                <Bar dataKey="total" fill="#6B3FA0" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="font-semibold mb-3 flex items-center gap-2"><Award className="h-4 w-4 text-[#FFB300]" /> Major donors (≥ {fmt$(data.threshold)} lifetime)</div>
          {data.major_donors.length === 0 ? <p className="text-sm text-gray-500">None yet.</p> : (
            <ol className="space-y-2 max-h-[220px] overflow-y-auto" data-testid="major-donors-list">
              {data.major_donors.map((d, i) => (
                <li key={d.id} className="flex justify-between text-sm">
                  <span>{i + 1}. {d.name}{d.org ? ` — ${d.org}` : ''}</span>
                  <span className="font-medium">{fmt$(d.total_given)}</span>
                </li>
              ))}
            </ol>
          )}
        </CardContent></Card>
      </div>

      {/* Lapsed donors */}
      <Card><CardContent className="p-4">
        <div className="font-semibold mb-3 flex items-center gap-2"><AlertCircle className="h-4 w-4 text-orange-600" /> Lapsed donors — gave last year, nothing this year</div>
        {data.lapsed.length === 0 ? <p className="text-sm text-gray-500">Nobody&apos;s lapsed yet — nice work!</p> : (
          <div className="space-y-1 max-h-[220px] overflow-y-auto" data-testid="lapsed-list">
            {data.lapsed.map(d => (
              <div key={d.id} className="flex justify-between text-sm border-b py-1.5">
                <span>{d.name}{d.org ? ` — ${d.org}` : ''}</span>
                <span className="text-gray-500 text-xs">Last: {d.last_gift_date || '—'} · {fmt$(d.total_given)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent></Card>

      {/* Heatmap */}
      <Card><CardContent className="p-4">
        <div className="font-semibold mb-3 flex items-center gap-2"><Activity className="h-4 w-4" /> Giving heatmap — last 365 days</div>
        <Heatmap data={data.heatmap} />
      </CardContent></Card>

      {/* Activity feed */}
      <Card><CardContent className="p-4">
        <div className="font-semibold mb-3">Recent activity</div>
        {data.activity.length === 0 ? <p className="text-sm text-gray-500">No gifts logged yet.</p> : (
          <ul className="space-y-2 max-h-[300px] overflow-y-auto" data-testid="activity-feed">
            {data.activity.map(g => (
              <li key={g.id} className="flex justify-between text-sm border-b py-1.5">
                <span className="flex items-center gap-2 flex-1 min-w-0">
                  {g.acknowledged ? <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" /> : <span className="w-4 shrink-0" />}
                  <span className="truncate">{g.donor_name}</span>
                  {g.campaign && <Badge variant="outline" className="text-[10px]">{g.campaign}</Badge>}
                  <span className="text-gray-500 text-xs">· {g.date}</span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className="font-medium">{fmt$(g.amount)}</span>
                  {!g.acknowledged && (
                    <button onClick={() => markAcked(g.id)}
                            className="text-xs text-[#0077B6] hover:underline"
                            data-testid={`activity-thank-${g.id}`}>
                      Thank
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent></Card>
    </div>
  );
}

function Heatmap({ data }) {
  // Build a 53-week grid from `data`: list of {date: YYYY-MM-DD, count}
  const byDate = Object.fromEntries((data || []).map(d => [d.date, d.count]));
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - 364);
  // Backfill to previous Sunday
  start.setDate(start.getDate() - start.getDay());
  const weeks = [];
  for (let w = 0; w < 53; w++) {
    const col = [];
    for (let d = 0; d < 7; d++) {
      const dt = new Date(start);
      dt.setDate(dt.getDate() + w * 7 + d);
      const iso = dt.toISOString().slice(0, 10);
      const count = byDate[iso] || 0;
      col.push({ iso, count });
    }
    weeks.push(col);
  }
  const color = (c) => c === 0 ? '#F3E5F5' : c === 1 ? '#CE93D8' : c === 2 ? '#AB47BC' : c === 3 ? '#8E24AA' : '#4A148C';
  return (
    <div className="overflow-x-auto">
      <div className="flex gap-1" data-testid="heatmap-grid">
        {weeks.map((col, wi) => (
          <div key={wi} className="flex flex-col gap-1">
            {col.map(({ iso, count }) => (
              <div key={iso}
                   title={`${iso}: ${count} gift${count === 1 ? '' : 's'}`}
                   className="w-3 h-3 rounded-sm"
                   style={{ backgroundColor: color(count) }} />
            ))}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-1 mt-2 text-[10px] text-gray-500">
        Less {[0,1,2,3,4].map(c => (
          <span key={c} className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: color(c) }} />
        ))} More
      </div>
    </div>
  );
}

// ==================== Import Tab ====================

function ImportTab({ api, onImported }) {
  const donorInputRef = useRef(null);
  const giftInputRef = useRef(null);
  const [donorResult, setDonorResult] = useState(null);
  const [giftResult, setGiftResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file, kind) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Choose a .csv file'); return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    const url = kind === 'donors' ? '/donors/import/csv' : '/donor-gifts/import/csv';
    try {
      const { data } = await api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (kind === 'donors') setDonorResult(data); else setGiftResult(data);
      toast.success(`Imported ${data.created}${data.updated ? ` (updated ${data.updated})` : ''} row${data.created + (data.updated || 0) === 1 ? '' : 's'}`);
      onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = (kind) => {
    const rows = kind === 'donors'
      ? [
          ['name', 'org', 'email', 'phone', 'address', 'pipeline_stage', 'tags', 'notes'],
          ['Jane Smith', 'Acme Foundation', 'jane@example.org', '555-0100', '123 Main St', 'donor', 'major-gift,board', 'Loves Explore Program'],
        ]
      : [
          ['donor_email', 'date', 'amount', 'campaign', 'gift_type', 'notes', 'acknowledged'],
          ['jane@example.org', '2025-11-15', '500.00', 'Fall Appeal 2025', 'one_time', 'Cash', 'true'],
        ];
    const csv = rows.map(r => r.map(c => /[,"]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = kind === 'donors' ? 'donors_template.csv' : 'gifts_template.csv';
    a.click();
  };

  const exportDonors = async () => {
    try {
      const resp = await api.get('/donors/export/csv', { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      a.download = 'donors_export.csv';
      a.click();
    } catch { toast.error('Export failed'); }
  };

  return (
    <div className="space-y-4" data-testid="import-tab">
      <Card><CardContent className="p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="font-semibold flex items-center gap-2"><Upload className="h-4 w-4" /> Import donors</div>
          <Button variant="outline" size="sm" onClick={() => downloadTemplate('donors')} data-testid="dl-donor-template">
            <Download className="h-4 w-4 mr-1" /> Template
          </Button>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          Accepts any of these header names (case-insensitive): <code className="bg-gray-100 px-1 rounded text-xs">name / full name / donor</code>, <code className="bg-gray-100 px-1 rounded text-xs">first name + last name</code>, <code className="bg-gray-100 px-1 rounded text-xs">email / e-mail</code>, <code className="bg-gray-100 px-1 rounded text-xs">org / organization / company</code>, <code className="bg-gray-100 px-1 rounded text-xs">phone</code>, <code className="bg-gray-100 px-1 rounded text-xs">address</code>, <code className="bg-gray-100 px-1 rounded text-xs">tags</code>, <code className="bg-gray-100 px-1 rounded text-xs">notes</code>, <code className="bg-gray-100 px-1 rounded text-xs">pipeline_stage / status</code>.
          Missing name → <em>“TO FILL”</em> placeholder + flagged. Existing donors (matched by email, else name) are updated and tags are merged.
        </p>
        <input ref={donorInputRef} type="file" accept=".csv"
               onChange={e => upload(e.target.files[0], 'donors')}
               className="text-sm" data-testid="donor-upload-input" />
        {donorResult && (
          <div className="mt-3 p-3 bg-gray-50 rounded text-sm">
            <div className="font-medium">Imported: {donorResult.created} new · {donorResult.updated} updated</div>
            {donorResult.needs_review > 0 && (
              <div className="text-orange-700 mt-1 flex items-center gap-1" data-testid="import-needs-review-count">
                <AlertCircle className="h-3.5 w-3.5" /> {donorResult.needs_review} row{donorResult.needs_review === 1 ? '' : 's'} flagged for review — open the Donors tab and toggle <em>“Needs review only”</em> to fix them.
              </div>
            )}
            {donorResult.errors?.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-red-600">{donorResult.errors.length} error(s)</summary>
                <ul className="mt-1 text-xs text-red-700">
                  {donorResult.errors.slice(0, 20).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
      </CardContent></Card>

      <Card><CardContent className="p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="font-semibold flex items-center gap-2"><Upload className="h-4 w-4" /> Import gifts</div>
          <Button variant="outline" size="sm" onClick={() => downloadTemplate('gifts')} data-testid="dl-gift-template">
            <Download className="h-4 w-4 mr-1" /> Template
          </Button>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          Columns: <code className="bg-gray-100 px-1 rounded text-xs">donor_email / donor_name, date, amount, campaign, gift_type, notes, acknowledged</code>.
          Missing donor → stub is auto-created and flagged. Missing date → today. Amount accepts <code className="bg-gray-100 px-1 rounded text-xs">$1,250.00</code>. Dates in <code className="bg-gray-100 px-1 rounded text-xs">M/D/YYYY</code> also work.
        </p>
        <input ref={giftInputRef} type="file" accept=".csv"
               onChange={e => upload(e.target.files[0], 'gifts')}
               className="text-sm" data-testid="gift-upload-input" />
        {giftResult && (
          <div className="mt-3 p-3 bg-gray-50 rounded text-sm">
            <div className="font-medium">Imported: {giftResult.created} gift(s)</div>
            {giftResult.needs_review > 0 && (
              <div className="text-orange-700 mt-1 flex items-center gap-1">
                <AlertCircle className="h-3.5 w-3.5" /> {giftResult.needs_review} gift{giftResult.needs_review === 1 ? '' : 's'} flagged (missing amount or date defaulted to today).
              </div>
            )}
            {giftResult.errors?.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-red-600">{giftResult.errors.length} error(s)</summary>
                <ul className="mt-1 text-xs text-red-700">
                  {giftResult.errors.slice(0, 20).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
      </CardContent></Card>

      <Card><CardContent className="p-5">
        <div className="font-semibold mb-2 flex items-center gap-2"><Download className="h-4 w-4" /> Export</div>
        <p className="text-sm text-gray-600 mb-3">
          Download the full donor list as CSV. Round-trips with the import format above.
        </p>
        <Button onClick={exportDonors} data-testid="export-donors-btn">
          <Download className="h-4 w-4 mr-1" /> Export donors.csv
        </Button>
      </CardContent></Card>

      <DangerZoneCard api={api} onReset={onImported} />
    </div>
  );
}

function DangerZoneCard({ api, onReset }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  if (user?.role !== 'administrator') return null;

  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/donor-data-reset', { confirm: confirmText.trim() });
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Donor portal reset — ${total} records erased`);
      setOpen(false); setConfirmText('');
      onReset();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    }
    setBusy(false);
  };

  return (
    <Card className="border-red-200" data-testid="danger-zone-card">
      <CardContent className="p-5">
        <div className="font-semibold mb-1 flex items-center gap-2 text-red-700">
          <AlertCircle className="h-4 w-4" /> Danger zone
        </div>
        <p className="text-sm text-gray-600 mb-3">
          Hard reset erases <strong>every</strong> donor, gift, timeline entry, task, pledge, segment, and household —
          a clean slate for a fresh import. Event ticket orders and Stripe payment records are kept
          (and will <em>not</em> re-create old donors). This cannot be undone.
        </p>
        <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => setOpen(true)} data-testid="hard-reset-open-btn">
          <Trash2 className="h-4 w-4 mr-1" /> Hard reset donor portal…
        </Button>
        <Dialog open={open} onOpenChange={o => { if (!o) { setOpen(false); setConfirmText(''); } }}>
          <DialogContent className="max-w-md" data-testid="hard-reset-dialog">
            <DialogHeader><DialogTitle className="text-red-700">Hard reset donor portal</DialogTitle></DialogHeader>
            <p className="text-sm text-gray-600">
              This permanently deletes ALL donor data. Consider clicking <strong>Export donors.csv</strong> first as a backup.
              Type <code className="bg-red-50 text-red-700 px-1 rounded font-bold">ERASE</code> to confirm.
            </p>
            <Input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder='Type ERASE'
                   data-testid="hard-reset-confirm-input" autoFocus />
            <DialogFooter>
              <Button variant="outline" onClick={() => { setOpen(false); setConfirmText(''); }}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white"
                      disabled={busy || confirmText.trim() !== 'ERASE'}
                      onClick={reset} data-testid="hard-reset-confirm-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Erase everything'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
